import Cookies from 'js-cookie';

import { get, post } from '@eika-infrastruktur/request';

import { AppState } from '../../reducers';
import {
    isFilopplastingsporsmal,
    toUploadedVedleggDTO,
    UploadedVedlegg,
} from '../../sporsmalsflyt/filopplasting/filopplasting-types';
import {
    besvarteSporsmalToSvarDTO,
    BesvartSporsmal,
    flattenBesvarteSporsmal,
} from '../../sporsmalsflyt/reducer/current-steg';
import {
    DateAsString,
    MeldSkadeResultWebDTO,
    Skademeldingstype,
    SporsmalsflytDTO,
    SporsmalsflytResultDTO,
    SporsmalsflytSkademeldingDTO,
} from '../../types/generated-types';
import { ForsikringOppsummeringInformation, ForsikringUnion } from '../../types/internal-types';
import { hentSporsmalsflytUrl, sendSkademeldingUrl } from '../../urler';
import { getOppsummeringForsikringOpplysningerDTO } from './skademeldingOppsummering-actions';

export const getRequestOptions = (bankregnr: string): any => ({
    headers: {
        'X-BankRegNr': bankregnr,
        'X-Csrf-Token': Cookies.get('X-Csrf-Token-meld-skade-web')!,
    },
});

export enum SkademeldingActionKeys {
    FetchingSporsmalsflyt = 'FETCHING_SPORSMALSFLYT',
    FetchedSporsmalsflyt = 'FETCHED_SPORSMALSFLYT',
    FetchingSporsmalsflytFailed = 'FETCHING_SPORSMALSFLYT_FAILED',
    SaveSkademelding = 'SAVE_SKADEMELDING',
    SaveSkademeldingSuccess = 'SAVE_SKADEMELDING_SUCCESS',
    SaveSkademeldingFailure = 'SAVE_SKADEMELDING_FAILURE',
    SaveSkademeldingVedleggFailure = 'SAVE_SKADEMELDING_FAILURE_VEDLEGG',
    SaveSkademeldingBadRequestFailure = 'SAVE_SKADEMELDING_FAILURE_BAD_REQUEST',
    SaveSkademeldingUnauthorisedFailure = 'SAVE_SKADEMELDING_FAILURE_UNAUTHORISED',
    ResetSavingState = 'RESET_SAVING_STATE',
    UpdateValidSession = 'UPDATE_VALID_SESSION',
    UpdateShouldRouteKundeToFremtind = 'UPDATE__SHOULD_ROUTE_KUNDE_TO_FREMTIND',
}

export interface FetchingSporsmalsflytAction {
    type: SkademeldingActionKeys.FetchingSporsmalsflyt;
    skadetype: Skademeldingstype;
}

export const fetchingSporsmalsflyt = (skadetype: Skademeldingstype): FetchingSporsmalsflytAction => ({
    type: SkademeldingActionKeys.FetchingSporsmalsflyt,
    skadetype,
});

export interface FetchedSporsmalsflytAction {
    type: SkademeldingActionKeys.FetchedSporsmalsflyt;
    sporsmalsflyt: SporsmalsflytDTO;
    fremtindSkadeforsikringKonvertingStartDate: DateAsString;
    enableRoutingToFremtind: boolean;
    fremtindUrl: string | undefined;
}

export const fetchedSporsmalsflyt = (
    sporsmalsflyt: SporsmalsflytDTO,
    fremtindSkadeforsikringKonvertingStartDate: DateAsString,
    enableRoutingToFremtind: boolean,
    fremtindUrl: string | undefined,
): FetchedSporsmalsflytAction => ({
    type: SkademeldingActionKeys.FetchedSporsmalsflyt,
    sporsmalsflyt,
    fremtindSkadeforsikringKonvertingStartDate,
    enableRoutingToFremtind,
    fremtindUrl,
});

export interface FetchingSporsmalsflytFailedAction {
    type: SkademeldingActionKeys.FetchingSporsmalsflytFailed;
}

export const fetchSporsmalsflytFailed = (): FetchingSporsmalsflytFailedAction => ({
    type: SkademeldingActionKeys.FetchingSporsmalsflytFailed,
});

export const getSporsmalsflytApi = async (
    skadetype: Skademeldingstype,
    bankregnr: string,
): Promise<SporsmalsflytResultDTO> => get(hentSporsmalsflytUrl(skadetype), getRequestOptions(bankregnr));

export const sendSkademeldingApi = async (
    sporsmalsflytSkademelding: SporsmalsflytSkademeldingDTO,
    bankregnr: string,
): Promise<MeldSkadeResultWebDTO> =>
    post(sendSkademeldingUrl(), sporsmalsflytSkademelding, getRequestOptions(bankregnr));

export interface SaveSkademeldingAction {
    type: SkademeldingActionKeys.SaveSkademelding;
}

export const saveSkademelding = (): SaveSkademeldingAction => ({
    type: SkademeldingActionKeys.SaveSkademelding,
});

export interface SaveSkademeldingSuccessAction {
    type: SkademeldingActionKeys.SaveSkademeldingSuccess;
    skademeldingResult?: MeldSkadeResultWebDTO;
}

export const saveSkademeldingSuccess = (
    skademeldingResult: MeldSkadeResultWebDTO | null,
): SaveSkademeldingSuccessAction => ({
    type: SkademeldingActionKeys.SaveSkademeldingSuccess,
    skademeldingResult,
});

export interface SaveSkademeldingFailureAction {
    type: SkademeldingActionKeys.SaveSkademeldingFailure;
}

export const saveSkademeldingFailure = (): SaveSkademeldingFailureAction => ({
    type: SkademeldingActionKeys.SaveSkademeldingFailure,
});

export interface SaveSkademeldigBadRequestFailureAction {
    type: SkademeldingActionKeys.SaveSkademeldingBadRequestFailure;
}

export const saveSkademeldingBadRequestFailure = (): SaveSkademeldigBadRequestFailureAction => ({
    type: SkademeldingActionKeys.SaveSkademeldingBadRequestFailure,
});

export interface SaveSkademeldigUnauthorisedFailureAction {
    type: SkademeldingActionKeys.SaveSkademeldingUnauthorisedFailure;
}

export const saveSkademeldingUnauthorisedFailure = (): SaveSkademeldigUnauthorisedFailureAction => ({
    type: SkademeldingActionKeys.SaveSkademeldingUnauthorisedFailure,
});

export interface SaveSkademeldingVedleggFailureAction {
    type: SkademeldingActionKeys.SaveSkademeldingVedleggFailure;
}

export const saveSkademeldingVedleggFailure = (): SaveSkademeldingVedleggFailureAction => ({
    type: SkademeldingActionKeys.SaveSkademeldingVedleggFailure,
});

export interface ValidSessionAction {
    type: SkademeldingActionKeys.UpdateValidSession;
}

export const updateValidSession = (): ValidSessionAction => ({
    type: SkademeldingActionKeys.UpdateValidSession,
});

export const updateShouldRouteKundeToFremtind = (
    forsikringer: ForsikringUnion[] | undefined,
    skadedato: DateAsString,
): UpdateShouldRouteKundeToFremtindAction => ({
    type: SkademeldingActionKeys.UpdateShouldRouteKundeToFremtind,
    forsikringer,
    skadedato,
});

export interface UpdateShouldRouteKundeToFremtindAction {
    type: SkademeldingActionKeys.UpdateShouldRouteKundeToFremtind;
    forsikringer: ForsikringUnion[] | undefined;
    skadedato: DateAsString;
}

export const sporsmalsflytskademeldingDTOFrom = (
    skadetype: Skademeldingstype,
    besvarteSporsmal: BesvartSporsmal[],
    forsikringOpplysninger?: ForsikringOppsummeringInformation,
    vedlegg?: UploadedVedlegg,
): SporsmalsflytSkademeldingDTO => ({
    skadetype,
    svar: besvarteSporsmalToSvarDTO(besvarteSporsmal),
    oppsummeringForsikringOpplysninger: forsikringOpplysninger
        ? getOppsummeringForsikringOpplysningerDTO(forsikringOpplysninger)
        : null,
    vedlegg: toUploadedVedleggDTO(vedlegg),
});

export enum VedleggSource {
    Skademelding,
    Klage,
}

export const getVedlegg = (state: AppState, vedleggSource: VedleggSource): UploadedVedlegg => {
    let source;
    switch (vedleggSource) {
        case VedleggSource.Klage:
            source = state.klage.sporsmalsflytState.besvarteSporsmal;
            break;
        default:
            source = state.skademelding.sporsmalsflytState.besvarteSporsmal;
    }

    const besvartFilopplastingssporsmal = flattenBesvarteSporsmal(source).find((besvartSporsmal) =>
        isFilopplastingsporsmal(besvartSporsmal),
    );

    if (besvartFilopplastingssporsmal && isFilopplastingsporsmal(besvartFilopplastingssporsmal)) {
        return {
            vedleggIds: besvartFilopplastingssporsmal.ids
                .filter((i) => i.id !== null)
                .map((i) => {
                    return {
                        id: i.id,
                        fileName: i.fileName,
                    };
                }),
        };
    }
    return { vedleggIds: [] };
};

export interface ResetSavingStateAction {
    type: SkademeldingActionKeys.ResetSavingState;
}

export const resetSavingState = () => ({
    type: SkademeldingActionKeys.ResetSavingState,
});
